import React, { useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as style from "./FeatureGroup.module.scss";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { featureGroupVisibilityToggle } from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import { Divider, Toggle } from "cprime-foundational-components";
import Typography from "../../Components/Common/CprimeText";

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme?.palette?.common?.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme?.palette?.common?.black,
    color: theme?.palette?.common?.white,
    fontSize: theme?.typography?.pxToRem(12),
    maxWidth: 300,
    border: "1px solid #dadde9",
    padding: theme?.spacing(1),
  },
}));
const Card: React.FC<{
  group: any;
  name: any;
  description: any;
  id: any;
  isExpanded: any;
  isEdit: any;
  isVisible: any;
}> = ({
  name,
  description,
  id,
  group,
  isExpanded,
  isEdit,
  isVisible: initialVisible,
}) => {
  const scssClass = style as any;
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(initialVisible);
  const navigate = useNavigate();

  const handleClick = (id: Number) => {
    navigate(`/edit-feature-group/${id}`);
  };

  const handleGroupVisibleToggle = async () => {
    const payload = {
      group_id: Number(id),
      is_visible: !isVisible,
    };

    try {
      const response = await featureGroupVisibilityToggle(payload);
      if (response?.error) {
        triggerToast(`${response?.error}`, "error");
      } else if (response?.status === "SUCCESS") {
        setIsVisible(!isVisible);
        triggerToast(`${response?.message}`, "success");
      }
    } catch (error) {}
  };

  return (
    <div
      className={scssClass.container}
      style={{
        boxShadow: "none",
        display: "flex",
        alignItems: "baseline",
        height: "100%",
      }}
      draggable={isEdit ? true : false}
      key={id}
    >
      {isEdit ? (
        <DragIndicatorIcon
          fontSize="small"
          className={scssClass["draggable-card-css"]}
          sx={{ marginLeft: "10px", cursor: "pointer" }}
        />
      ) : null}
      <Box style={{ width: "100%" }}>
        <div className={scssClass.featureSection}>
          <div
            className="ml-4 space-y-1"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div className={scssClass.featureItem}>
              <div
                className={scssClass.featureRow}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div className={scssClass.gridrow}>
                  <div className={scssClass.gridcol}>
                    <Typography
                      ref={ref}
                      className={scssClass.groupName}
                      noWrap
                      sx={{
                        fontSize: "20px !important",
                        lineHeight: "28px !important",
                        fontWeight: "600 !important",
                        color: "#000000 !important",
                      }}
                    >
                      {name ? name : ""}
                    </Typography>
                  </div>
                </div>
                <div className={scssClass.gridcol2}>
                  <div
                    style={{
                      display: !isEdit ? "none" : "",
                    }}
                    className={scssClass.gridcol1}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        disabled={false}
                        style={{
                          margin: "0",
                          padding: "0",
                          color: "#000",
                          display: "flex",
                          marginRight: "2px",
                        }}
                        onClick={() => handleClick(id)}
                      >
                        <EditIcon />
                      </IconButton>
                      <Box>
                        <Toggle
                          checked={isVisible}
                          sx={{
                            overflow: "visible",
                            marginBottom: "4px",
                            "& .MuiSwitch-thumb": {
                              marginTop: "4px",
                            },
                          }}
                          size="small"
                          data-testid={`toggle`}
                          onChange={handleGroupVisibleToggle}
                        />
                      </Box>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ boxShadow: "none", paddingBottom: "32px" }}>
          <div className={scssClass.header}>
            <Typography
              className={scssClass.groupDescription}
              style={{
                fontSize: "14px",
                padding: "4px 0px",
              }}
            >
              {description || ""}
            </Typography>
          </div>
        </div>

        {isExpanded && (
          <div className={scssClass.featureSection} style={{ marginTop: "0" }}>
            <div
              className="ml-4 space-y-1"
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {group?.features?.map((feature: any, index: number) => (
                <div className={scssClass.featureItem} key={index}>
                  <div
                    className={scssClass.featureRow}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      opacity: feature?.is_open_to_all === false ? 0.5 : 1, // Updated this for feature visibility
                    }}
                  >
                    <div className={scssClass.gridrow}>
                      <div className={scssClass.gridcol}>
                        <span
                          style={{
                            fontSize: "14px",
                            lineHeight: "18px",
                            fontWeight: "400",
                            color: "#000",
                          }}
                        >
                          {feature?.featureName}
                        </span>
                      </div>
                    </div>
                    <div className={scssClass.gridcol2}>
                      <CustomTooltip
                        title={
                          feature?.featureDescription === null
                            ? "No description available"
                            : feature?.featureDescription
                        }
                      >
                        <IconButton
                          size="medium"
                          sx={{
                            "&.MuiSvgIcon-root": {
                              fill: "#000",
                              fontSize: "16px",
                            },
                          }}
                        >
                          <InfoOutlinedIcon
                            fontSize="medium"
                            sx={{
                              "&.MuiSvgIcon-root": {
                                fill: "#000",
                                fontSize: "16px",
                              },
                            }}
                          />
                        </IconButton>
                      </CustomTooltip>
                    </div>
                  </div>
                  {group?.features?.length - 1 !==
                    group?.features?.indexOf(feature) && (
                    <Divider
                      variant={"middle"}
                      sx={{ borderColor: "#d9d9d9", margin: "12px 10px" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Box>
    </div>
  );
};

export default Card;
